import { useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'

import { FaqLearnArticleLink } from '@/features/aprender/FaqLearnArticleLink'
import { FAQ_CONCEPT_ARTICLE_IDS } from '@/features/aprender/faqConceptArticleLinks'
import { useSectionViewTrack } from '@/hooks/useSectionViewTrack'
import { ROUTES } from '@/navigation/constants'

import { FOOD_COST_LANDING_PAGE } from './constants'

const FAQ_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'] as const

interface FoodCostFaqSectionProps {
    isDirectConversion?: boolean
}

export function FoodCostFaqSection({ isDirectConversion = false }: FoodCostFaqSectionProps) {
    const { t } = useTranslation()
    const sectionRef = useRef(null)
    const [openIndex, setOpenIndex] = useState<number | null>(null)

    useSectionViewTrack(sectionRef, 'FAQ', { landing_page: FOOD_COST_LANDING_PAGE })

    const faqAnswer = (faqKey: (typeof FAQ_KEYS)[number]) => {
        if (isDirectConversion && (faqKey === '5' || faqKey === '6')) {
            return t(`foodCost.faq.items.${faqKey}.answerDirect`)
        }
        return t(`foodCost.faq.items.${faqKey}.answer`)
    }

    return (
        <section ref={sectionRef} id='faqs' className='bg-[#F5EEE4] px-[22px] py-12 md:px-10 md:py-20'>
            <div className='mx-auto w-full max-w-[640px] md:max-w-[720px]'>
                <div className='mb-5 flex flex-col gap-2.5'>
                    <h2 className='m-0 text-[32px] font-medium leading-[1.15] tracking-[-1px] text-[#F4502A] md:text-[46px]'>
                        {t('foodCost.faq.title')}
                    </h2>
                    <p className='m-0 max-w-[48ch] text-[15px] leading-[1.65] text-[#5C5548] md:text-[17px] md:leading-[1.7]'>
                        {t('foodCost.faq.answerCapsule')}
                    </p>
                </div>

                <div>
                    {FAQ_KEYS.map((faqKey, index) => {
                        const isOpen = openIndex === index
                        return (
                            <div key={faqKey} className='border-t border-[#D8CFC0]'>
                                <button
                                    type='button'
                                    aria-expanded={isOpen}
                                    onClick={() => setOpenIndex(isOpen ? null : index)}
                                    className='flex min-h-11 w-full items-start justify-between gap-3.5 bg-transparent py-[18px] text-left'
                                >
                                    <span className='text-base font-semibold leading-snug text-[#17130E] md:text-[17px]'>
                                        {t(`foodCost.faq.items.${faqKey}.question`)}
                                    </span>
                                    <span
                                        className='shrink-0 text-[22px] font-medium leading-none text-[#F4502A]'
                                        aria-hidden
                                    >
                                        {isOpen ? '−' : '+'}
                                    </span>
                                </button>
                                <div
                                    className={`max-w-[34ch] pb-[18px] text-[15px] leading-[1.65] text-[#5C5548] md:max-w-[48ch] ${isOpen ? '' : 'hidden'}`}
                                    aria-hidden={!isOpen}
                                >
                                    {faqAnswer(faqKey)}
                                    {faqKey === '1' ? (
                                        <FaqLearnArticleLink articleId={FAQ_CONCEPT_ARTICLE_IDS['1']} />
                                    ) : null}
                                </div>
                            </div>
                        )
                    })}
                </div>

                <div className='border-t border-[#D8CFC0] pt-5'>
                    <Link
                        to={ROUTES.faqs}
                        className='text-sm font-semibold leading-relaxed text-[#F4502A] no-underline'
                    >
                        {t('foodCost.faq.seeAll')}
                    </Link>
                </div>
            </div>
        </section>
    )
}
